/**
 * @file        src/lib/tariffs/thirtyMinutePower.ts
 * @description 30-минутная мощность электромотора, кВт — вход утильсбора для электромобилей и
 *              последовательных гибридов (ПП 1291, раздел I, сноска 2) и акциза электромобилей
 *              (письма Минфина 03-13-12/5697 от 29.01.2021, ФНС СД-4-3/1137@ от 02.02.2021).
 *              Берётся из документов (ОТТС / СБКТС / ЭПТС), а если их нет — оценивается как
 *              пиковая × 0,45: так с февраля 2026 делают испытательные лаборатории для моделей без
 *              данных о 30-минутной мощности (решение владельца В1, CALC-NORM-01).
 *              У обычного гибрида (HEV / PHEV / мягкий) вход утильсбора — мощность ДВС + 30-минутная
 *              мощность электромотора по тому же правилу (решение В4, CAT-POWER-02).
 * @rule        Никакого округления: результат идёт в сравнение с порогами кВт как есть.
 * @rule        Единственное место, где живёт коэффициент 0,45 и выбор «документы / оценка».
 */

import { hpToKw } from '@/lib/units/power';

/** Оценка 30-минутной мощности по пиковой при отсутствии документов (практика лабораторий с 02.2026). */
export const EV_PEAK_TO_30MIN = 0.45;

/** Откуда взята 30-минутная мощность: из документов или оценка по пиковой. */
export type ThirtyMinPowerSource = 'documents' | 'estimate';

export interface ThirtyMinPower {
  /** 30-минутная мощность, кВт, без округления. */
  kw: number;
  source: ThirtyMinPowerSource;
}

/**
 * Resolve the 30-minute motor power used by the tariffs.
 * @input  peakHp — пиковая мощность, л.с. (то, что есть в каталоге и в форме калькулятора);
 *         documentedKw — 30-минутная мощность из документов, кВт (необязательно)
 * @output { kw, source } — documentedKw as-is when it is a positive finite number, otherwise
 *         hpToKw(peakHp) × 0,45 (unrounded) with source 'estimate'
 * @important documentedKw ≤ 0, NaN or absent all mean "unknown" — never a real zero power.
 */
export function resolveThirtyMinPowerKw(peakHp: number, documentedKw?: number | null): ThirtyMinPower {
  if (typeof documentedKw === 'number' && Number.isFinite(documentedKw) && documentedKw > 0) {
    return { kw: documentedKw, source: 'documents' };
  }
  return { kw: hpToKw(peakHp) * EV_PEAK_TO_30MIN, source: 'estimate' };
}

/** Мощность гибрида для утильсбора: ДВС + 30-минутная мощность электромотора (В4). */
export interface HybridUtilPower {
  /** Мощность ДВС, кВт, без округления. */
  iceKw: number;
  /** 30-минутная мощность электромотора: документы или пиковая × 0,45. */
  motor: ThirtyMinPower;
  /** iceKw + motor.kw — вход утильсбора, кВт. */
  totalKw: number;
}

/** A positive finite number, else false. */
function isPositive(n: number | null | undefined): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0;
}

/**
 * Power of a parallel/mild hybrid for the recycling fee: ICE power + 30-minute power of the
 * electric motor (ПП 1291, раздел I, сноска 2; решение владельца В4, CAT-POWER-02).
 * @input  iceHp — мощность ДВС, л.с.; motorPeakHp — пиковая мощность электромотора(ов), л.с.;
 *         motorDocumentedKw — 30-минутная мощность мотора из документов, кВт (необязательно)
 * @output HybridUtilPower, либо undefined, если ДВС или мотор неизвестны
 * @important undefined means "no hybrid data" — the caller then prices exactly as before
 *            (single enginePower). The 30-minute rule for the motor is the SAME function as for
 *            EV (resolveThirtyMinPowerKw), so the 0,45 factor still lives only in this file.
 */
export function resolveHybridUtilPowerKw(
  iceHp: number | null | undefined,
  motorPeakHp: number | null | undefined,
  motorDocumentedKw?: number | null,
): HybridUtilPower | undefined {
  if (!isPositive(iceHp) || !isPositive(motorPeakHp)) return undefined;
  const iceKw = hpToKw(iceHp);
  const motor = resolveThirtyMinPowerKw(motorPeakHp, motorDocumentedKw);
  return { iceKw, motor, totalKw: iceKw + motor.kw };
}
